import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ImageIcon, MonitorCog, Palette, Save } from 'lucide-react'
import { toast } from 'sonner'

import NodeAddressManager from '@/components/tree/node-address-manager'
import NodePaymentMethodManager from '@/components/tree/node-payment-method-manager'
import NodeSettingManager from '@/components/tree/node-setting-manager'
import ServiceInstanceCredentialManager from '@/components/tree/service-instance-credential-manager'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { orgTreeApi, type OrgTreeNodeConfig } from '@/services/org-tree'

const textTabs = [
  { value: 'privacy-policy', label: 'Privacy Policy', field: 'privacy_policy' as const },
  { value: 'terms-and-condition', label: 'Terms And Condition', field: 'terms_and_conditions' as const },
  { value: 'about-us', label: 'About Us', field: 'about_us' as const },
  { value: 'copyright', label: 'Copyright', field: 'copyright' as const },
  { value: 'return-policy', label: 'Return Policy', field: 'return_details' as const },
  { value: 'delivery-policy', label: 'Delivery Policy', field: 'refund_details' as const },
]

const tabOrder = [
  'address',
  ...textTabs.map((tab) => tab.value),
  'banner',
  'logo',
  'social-media',
  'contacts',
  'payments-method',
  'credentials',
  'color-code',
  'ui-version',
]

export default function TreeClientDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const storeName = searchParams.get('name')
  const sub = searchParams.get('sub')
  const clientMode = searchParams.get('client') === 'client'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'address')
  const [draftConfig, setDraftConfig] = useState<OrgTreeNodeConfig>({})

  const nodeQuery = useQuery({
    queryKey: ['org-tree-node', id],
    queryFn: () => orgTreeApi.getNode(id!),
    enabled: Boolean(id),
  })

  const configQuery = useQuery({
    queryKey: ['org-tree-config', id],
    queryFn: () => orgTreeApi.getNodeConfig(id!),
    enabled: Boolean(id),
  })

  const instanceQuery = useQuery({
    queryKey: ['org-tree-service-instance', sub],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(sub!),
    enabled: Boolean(sub),
  })

  const updateConfigMutation = useMutation({
    mutationFn: (payload: Partial<OrgTreeNodeConfig>) => orgTreeApi.updateNodeConfig(id!, payload),
    onSuccess: async () => {
      toast.success('Store details updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-config', id] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  useEffect(() => {
    if (configQuery.data) {
      setDraftConfig(configQuery.data)
    }
  }, [configQuery.data])

  useEffect(() => {
    const nextTab = searchParams.get('tab') || 'address'
    if (tabOrder.includes(nextTab)) {
      setActiveTab(nextTab)
    }
  }, [searchParams])

  const paymentOrgInstanceId = sub || serviceInstanceId

  const backPath = clientMode
    ? `/org/node/subclient-add/${localStorage.getItem('node_id') || id}?client=client`
    : `/org/node/client-add/${localStorage.getItem('node_id') || id}`

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    const next = new URLSearchParams(searchParams)
    if (value === 'address') {
      next.delete('tab')
    } else {
      next.set('tab', value)
    }
    setSearchParams(next, { replace: true })
  }

  const renderTextEditor = (field: keyof OrgTreeNodeConfig, title: string, description: string) => (
    <Card className="rounded-[24px] border-border/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea value={String(draftConfig[field] ?? '')} onChange={(event) => setDraftConfig((current) => ({ ...current, [field]: event.target.value }))} rows={10} />
        <div className="flex justify-end">
          <Button onClick={() => updateConfigMutation.mutate({ [field]: draftConfig[field] ?? '' })} disabled={updateConfigMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  if (nodeQuery.isLoading || configQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-[28px]" />
        <Skeleton className="h-[36rem] w-full rounded-[28px]" />
      </div>
    )
  }

  if (nodeQuery.error || configQuery.error || !nodeQuery.data) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load vendor details</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(nodeQuery.error || configQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId(backPath, serviceInstanceId))}>Back</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{storeName || nodeQuery.data.node_name || 'Store'} Policies and Information</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                Vendor-specific details page, including store policies, contact data, and service-instance media tabs.
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to={withServiceInstanceId(backPath, serviceInstanceId)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="overflow-x-auto rounded-[28px] border border-border/80 bg-card p-3">
          <TabsList className="inline-flex h-auto min-w-max gap-2 bg-transparent p-0">
            <TabsTrigger value="address">Address</TabsTrigger>
            {textTabs.map((tab) => <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>)}
            <TabsTrigger value="banner">Banner</TabsTrigger>
            <TabsTrigger value="logo">Logo</TabsTrigger>
            <TabsTrigger value="social-media">Social Media</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="payments-method">Wallet</TabsTrigger>
            <TabsTrigger value="credentials">Credentials</TabsTrigger>
            <TabsTrigger value="color-code">Color Code</TabsTrigger>
            <TabsTrigger value="ui-version">Ui-Version</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="address" className="mt-0">
          <NodeAddressManager
            nodeId={id!}
            description="The primary address recorded for this store node."
          />
        </TabsContent>

        {textTabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="mt-0">
            {renderTextEditor(tab.field, tab.label, `Update the ${tab.label.toLowerCase()} text for this store.`)}
          </TabsContent>
        ))}

        <TabsContent value="banner" className="mt-0">
          <Card className="rounded-[24px] border-border/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5 text-primary" /> Banner</CardTitle>
              <CardDescription>Banner-style media derived from the linked service branch.</CardDescription>
            </CardHeader>
            <CardContent>
              {instanceQuery.data?.welcome_page_logo || instanceQuery.data?.login_page_logo ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[instanceQuery.data.welcome_page_logo, instanceQuery.data.login_page_logo].filter(Boolean).map((image, index) => (
                    <div key={index} className="overflow-hidden rounded-2xl border border-border/70 bg-background p-4">
                      <img src={String(image)} alt="Banner asset" className="max-h-64 w-full object-contain" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-center text-sm text-muted-foreground">No banner assets are configured on the linked service branch.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logo" className="mt-0">
          <Card className="rounded-[24px] border-border/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5 text-primary" /> Logo</CardTitle>
              <CardDescription>Primary logo assets from the linked service branch.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {instanceQuery.data?.logo || instanceQuery.data?.side_panel_logo ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[instanceQuery.data.logo, instanceQuery.data.side_panel_logo].filter(Boolean).map((image, index) => (
                    <div key={index} className="overflow-hidden rounded-2xl border border-border/70 bg-background p-4">
                      <img src={String(image)} alt="Logo asset" className="max-h-64 w-full object-contain" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-center text-sm text-muted-foreground">No logo assets are configured on the linked service branch.</div>
              )}
              {sub ? (
                <div className="flex justify-end">
                  <Button variant="outline" asChild>
                    <Link to={withServiceInstanceId(`/org/node/service-instrance/media-form/${sub}?nodeId=${id}`, serviceInstanceId)}>Open Media Form</Link>
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social-media" className="mt-0">
          <NodeSettingManager
            nodeId={id!}
            mode="social"
            title="Social Media"
            description="Social links recorded against this store."
          />
        </TabsContent>

        <TabsContent value="contacts" className="mt-0">
          <NodeSettingManager
            nodeId={id!}
            mode="contact"
            title="Contact"
            description="Store contact methods."
          />
        </TabsContent>

        <TabsContent value="payments-method" className="mt-0">
          <NodePaymentMethodManager nodeId={id!} orgInstanceId={paymentOrgInstanceId} />
        </TabsContent>

        <TabsContent value="credentials" className="mt-0">
          {paymentOrgInstanceId ? (
            <ServiceInstanceCredentialManager
              serviceInstanceId={paymentOrgInstanceId}
              title="Service Branch Credential Manager"
              description="View and update the effective credential set used by this store, with branch override and organisation fallback visibility."
            />
          ) : (
            <Card className="rounded-[24px] border-border/80">
              <CardHeader>
                <CardTitle>Credential Manager</CardTitle>
                <CardDescription>No linked service branch is available for credential management.</CardDescription>
              </CardHeader>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="color-code" className="mt-0">
          <Card className="rounded-[24px] border-border/80"><CardHeader><CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Color Code</CardTitle><CardDescription>Store branding color settings from the legacy setup page.</CardDescription></CardHeader><CardContent><div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-sm text-muted-foreground">Color-code configuration is not exposed by the current portal APIs. This tab preserves the route and navigation point.</div></CardContent></Card>
        </TabsContent>

        <TabsContent value="ui-version" className="mt-0">
          <Card className="rounded-[24px] border-border/80"><CardHeader><CardTitle className="flex items-center gap-2"><MonitorCog className="h-5 w-5 text-primary" /> Ui-Version</CardTitle><CardDescription>Legacy placeholder for store-specific UI version settings.</CardDescription></CardHeader><CardContent><div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-sm text-muted-foreground">UI-version management is not currently surfaced in the admin portal.</div></CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}